import { apiFetch } from "../apiClient";
import { officeHostHeaders } from "../office/officeHost";
import { isAbortError } from "../utils/abortError";
import { parseErrorResponse } from "../utils/apiError";
import { checkAuthStatus } from "../utils/authStatus";
import {
  isSkill,
  isSkillListResponse,
  isSkillsResolveRequest,
  isSkillsResolveResponse,
} from "./guards";
import type {
  Skill,
  SkillSummary,
  SkillsResolveMode,
  SkillsResolveRequest,
  SkillsResolveResponse,
} from "./types";

export const PERSONAL_SKILLS_BFF_PATH = "/api/finster/skills";
export const SKILL_DIRECTORY_BFF_PATH = "/api/finster/skill-directory";
export const SKILLS_RESOLVE_BFF_PATH = "/api/finster/skills/resolve";

export type SkillsClientOperation =
  | "listPersonal"
  | "getPersonal"
  | "listDirectory"
  | "getDirectory"
  | "resolve";

export type SkillsClientErrorKind =
  | "auth"
  | "validation"
  | "not_found"
  | "conflict"
  | "timeout"
  | "rate_limit"
  | "upstream"
  | "network"
  | "contract"
  | "aborted";

export type SkillsResolveFailureDisposition =
  | "fail-open"
  | "fail-closed";

export class SkillsClientError extends Error {
  readonly kind: SkillsClientErrorKind;
  readonly operation: SkillsClientOperation;
  readonly status: number;
  readonly resolveDisposition?: SkillsResolveFailureDisposition;

  constructor(
    kind: SkillsClientErrorKind,
    operation: SkillsClientOperation,
    message: string,
    options: {
      status?: number;
      resolveDisposition?: SkillsResolveFailureDisposition;
    } = {}
  ) {
    super(message);
    this.name = "SkillsClientError";
    this.kind = kind;
    this.operation = operation;
    this.status = options.status ?? 0;
    this.resolveDisposition = options.resolveDisposition;
    Object.setPrototypeOf(this, SkillsClientError.prototype);
  }
}

function isAutomaticFailOpenKind(kind: SkillsClientErrorKind): boolean {
  return (
    kind === "not_found" ||
    kind === "timeout" ||
    kind === "rate_limit" ||
    kind === "upstream" ||
    kind === "network" ||
    kind === "contract"
  );
}

function resolveDisposition(
  mode: SkillsResolveMode | undefined,
  kind: SkillsClientErrorKind
): SkillsResolveFailureDisposition | undefined {
  if (!mode) return undefined;
  return mode === "automatic" && isAutomaticFailOpenKind(kind)
    ? "fail-open"
    : "fail-closed";
}

function createClientError(
  kind: SkillsClientErrorKind,
  operation: SkillsClientOperation,
  message: string,
  status = 0,
  resolveMode?: SkillsResolveMode
): SkillsClientError {
  return new SkillsClientError(kind, operation, message, {
    status,
    resolveDisposition: resolveDisposition(resolveMode, kind),
  });
}

function mapHttpKind(status: number): SkillsClientErrorKind {
  if (status === 401 || status === 403) return "auth";
  if (status === 400 || status === 413 || status === 422) {
    return "validation";
  }
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 408 || status === 504) return "timeout";
  if (status === 429) return "rate_limit";
  return "upstream";
}

async function parseSkillsErrorResponse(
  response: Response
): Promise<string> {
  try {
    const payload = (await response.clone().json()) as unknown;
    if (payload && typeof payload === "object" && !Array.isArray(payload)) {
      const detail = (payload as Record<string, unknown>).detail;
      if (typeof detail === "string" && detail.trim()) {
        return detail.trim();
      }
    }
  } catch {
    // Fall through to the shared BFF error parser.
  }
  return parseErrorResponse(response);
}

/**
 * Names the Office application the request came from so upstream traces are
 * attributed to the host that asked. When the host has not been probed yet the
 * header is left off: the add-in server drops values it does not recognize, so
 * an omitted header costs attribution, while a guessed one would credit the
 * wrong application.
 */
function withOfficeHost(init: RequestInit): RequestInit {
  const hostHeaders = officeHostHeaders();
  if (Object.keys(hostHeaders).length === 0) return init;
  const headers = new Headers(init.headers);
  for (const [name, value] of Object.entries(hostHeaders)) {
    headers.set(name, value);
  }
  return { ...init, headers };
}

async function requestJson(
  path: string,
  init: RequestInit,
  operation: SkillsClientOperation,
  resolveMode?: SkillsResolveMode
): Promise<unknown> {
  let response: Response;
  try {
    response = await apiFetch(path, withOfficeHost(init));
  } catch (error) {
    const aborted = init.signal?.aborted || isAbortError(error);
    throw createClientError(
      aborted ? "aborted" : "network",
      operation,
      aborted
        ? "Skills request was cancelled."
        : "Skills service is unavailable right now.",
      0,
      resolveMode
    );
  }

  checkAuthStatus(response);
  if (!response.ok) {
    const message = await parseSkillsErrorResponse(response);
    const kind = mapHttpKind(response.status);
    throw createClientError(
      kind,
      operation,
      message || `Skills request failed with HTTP ${response.status}.`,
      response.status,
      resolveMode
    );
  }

  try {
    return await response.json();
  } catch {
    throw createClientError(
      "contract",
      operation,
      "Skills service returned a non-JSON response.",
      502,
      resolveMode
    );
  }
}

async function listSkillsAt(
  path: string,
  operation: "listPersonal" | "listDirectory",
  signal?: AbortSignal
): Promise<SkillSummary[]> {
  const payload = await requestJson(
    path,
    { method: "GET", signal },
    operation
  );
  if (!isSkillListResponse(payload)) {
    throw createClientError(
      "contract",
      operation,
      "Skills service returned an invalid list response.",
      502
    );
  }
  return payload.items;
}

async function getSkillAt(
  path: string,
  operation: "getPersonal" | "getDirectory",
  signal?: AbortSignal
): Promise<Skill> {
  const payload = await requestJson(
    path,
    { method: "GET", signal },
    operation
  );
  if (!isSkill(payload)) {
    throw createClientError(
      "contract",
      operation,
      "Skills service returned an invalid detail response.",
      502
    );
  }
  return payload;
}

export function listPersonalSkills(
  signal?: AbortSignal
): Promise<SkillSummary[]> {
  return listSkillsAt(PERSONAL_SKILLS_BFF_PATH, "listPersonal", signal);
}

export function getPersonalSkill(
  skillId: string,
  signal?: AbortSignal
): Promise<Skill> {
  return getSkillAt(
    `${PERSONAL_SKILLS_BFF_PATH}/${encodeURIComponent(skillId)}`,
    "getPersonal",
    signal
  );
}

export function listDirectorySkills(
  signal?: AbortSignal
): Promise<SkillSummary[]> {
  return listSkillsAt(
    SKILL_DIRECTORY_BFF_PATH,
    "listDirectory",
    signal
  );
}

export function getDirectorySkill(
  skillId: string,
  signal?: AbortSignal
): Promise<Skill> {
  return getSkillAt(
    `${SKILL_DIRECTORY_BFF_PATH}/${encodeURIComponent(skillId)}`,
    "getDirectory",
    signal
  );
}

function getResolveMode(request: SkillsResolveRequest): SkillsResolveMode {
  return Object.prototype.hasOwnProperty.call(request, "skill_ids")
    ? "explicit"
    : "automatic";
}

export async function resolveSkills(
  request: SkillsResolveRequest,
  signal?: AbortSignal
): Promise<SkillsResolveResponse> {
  const mode = getResolveMode(request);
  if (!isSkillsResolveRequest(request)) {
    throw createClientError(
      "validation",
      "resolve",
      "Skills resolve request is invalid.",
      0,
      mode
    );
  }

  const normalizedSubmissionId = request.submission_id.trim();
  let body: string;
  try {
    body = JSON.stringify({
      ...request,
      submission_id: normalizedSubmissionId,
    });
  } catch {
    throw createClientError(
      "validation",
      "resolve",
      "Skills resolve request must be JSON-serializable.",
      0,
      mode
    );
  }

  const payload = await requestJson(
    SKILLS_RESOLVE_BFF_PATH,
    {
      method: "POST",
      body,
      signal,
    },
    "resolve",
    mode
  );
  if (
    !isSkillsResolveResponse(payload) ||
    payload.submission_id !== normalizedSubmissionId
  ) {
    throw createClientError(
      "contract",
      "resolve",
      "Skills resolver returned an invalid response.",
      502,
      mode
    );
  }
  return payload;
}

export function isFailOpenSkillsResolveError(
  error: unknown
): error is SkillsClientError & {
  resolveDisposition: "fail-open";
} {
  return (
    error instanceof SkillsClientError &&
    error.resolveDisposition === "fail-open"
  );
}

export const listSkills = listPersonalSkills;
export const getSkill = getPersonalSkill;
